
import type { PropType } from 'vue'
import type { FiltersMessages, RegularFilter, RegularFilterConfig } from '../../models'
import type { EpbSelectOption } from '../shared/epb-select'
import { computed, defineComponent, getCurrentInstance, onMounted, ref, watch } from 'vue'
import { FilterIdEnum } from '../../enums'
import { capitalizeSentence } from '../../filters/capitalize-sentence.filter'
import { EpbSelect } from '../shared/epb-select'

export default defineComponent({
  name: 'RegularFilterSelector',
  components: {
    EpbSelect,
  },
  props: {
    filter: {
      type: Object as PropType<RegularFilter>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    round: Boolean,
    showLabel: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      required: false,
    },
  },
  setup(props, { emit }) {
    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const baseClass = 'epb-regular-filter-selector'

    const filtersMessages = ctx.proxy.$epb.messages.filters as FiltersMessages

    const filterValues = ref<Array<RegularFilterConfig['values'][number]>>(
      props.filter.config.values ?? [],
    )

    const loadingFilterValues = ref(false)

    const isFilterOptional = computed<boolean>(() => !props.filter.config.mandatory)

    const isFilterMultiple = computed<boolean>(
      () => !props.filter.config.multiselectDisabled && props.filter.config.values.length > 1,
    )

    const showSelectAll = computed<boolean>(() => isFilterOptional.value || isFilterMultiple.value)

    const options = computed<Array<EpbSelectOption>>(() => {
      const result = filterValues.value.map<EpbSelectOption>(filterValueConfig => {
        const filterValueLabel =
          filterValueConfig.prettyName ??
          (filtersMessages.values[props.filter.config.id] as Record<string, string>)?.[
            filterValueConfig.id
          ] ??
          filterValueConfig.id
        return {
          value: filterValueConfig.id,
          label: capitalizeSentence(filterValueLabel),
        }
      })
      if (!props.filter.config.sortValuesDisabled) {
        result.sort(compareOptions)
      }
      return result
    })

    const value = computed<string | Array<string>>(() => {
      let result: string | Array<string>
      if (props.filter.config.multiselectDisabled) {
        const allSelected = props.filter.selectedValues.length === options.value.length
        if (showSelectAll.value && allSelected) {
          result = ''
        } else {
          result = props.filter.selectedValues[0] ?? ''
        }
      } else {
        result = props.filter.selectedValues
      }
      return result
    })

    const label = computed<string>(() => {
      if (props.showLabel !== true) {
        return ''
      }

      const result =
        props.filter.config.prettyName ??
        (filtersMessages.names[props.filter.config.id] as string) ??
        props.filter.config.id
      return capitalizeSentence(result)
    })

    const selectAllLabel = computed(() => {
      return props.filter.config.emptyMeansNone
        ? filtersMessages.values.none
        : filtersMessages.values.all.default
    })

    const placeholder = computed<string>(() => {
      if (props.placeholder) {
        return props.placeholder
      }

      let result: string
      if (!props.filter.config.mandatory) {
        result = selectAllLabel.value
      } else {
        result = props.filter.config.multiselectDisabled
          ? filtersMessages.placeholder.single
          : filtersMessages.placeholder.multiple
      }
      return result
    })

    function change(newValue: string | Array<string>): void {
      const payload = Array.isArray(newValue) ? newValue : newValue ? [newValue] : []
      emit('selectedValuesChange', payload)
    }

    function compareOptions(option1: EpbSelectOption, option2: EpbSelectOption): number {
      let result: number
      if (props.filter.config.id === FilterIdEnum.DEVICE) {
        const devices = Object.keys(filtersMessages.values[FilterIdEnum.DEVICE])
        result = devices.indexOf(option1.value) - devices.indexOf(option2.value)
      } else {
        result = option1.label.localeCompare(option2.label)
      }
      return result
    }

    watch(
      () => props.filter.config.values,
      values => {
        if (typeof props.filter.config.getAsyncValues === 'function') {
          return
        }
        filterValues.value = values
      },
    )

    onMounted(async () => {
      if (typeof props.filter.config.getAsyncValues === 'function') {
        loadingFilterValues.value = true
        try {
          filterValues.value = await props.filter.config.getAsyncValues()
        } finally {
          loadingFilterValues.value = false
        }
      }
    })

    return {
      baseClass,
      showSelectAll,
      value,
      options,
      label,
      placeholder,
      selectAllLabel,
      loadingFilterValues,
      change,
    }
  },
})
